import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  styled,
  Container,
  useMediaQuery,
  Tooltip,
  Fab,
  Zoom,
  Grid,
  Stack,
  Divider,
  Menu,
  MenuItem,
  ListItemButton
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  MedicalServices,
  ContactPhone,
  CalendarMonth,
  AdminPanelSettings,
  WhatsApp,
  Phone,
  Close,
  Place,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  KeyboardArrowDown,
  Schedule,
  Article
} from '@mui/icons-material';
import { Routes, Route, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import Home from './components/Home';
import Services from './components/Services';
import ContactUs from './components/ContactUs';
import BookAppointment from './components/BookAppointment';
import AdminPanel from './components/AdminPanel';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ServiceDetail from './components/pages/ServiceDetail';
import SportsPhysiotherapy from './components/services/SportsPhysiotherapy';
import OrthopedicPhysiotherapy from './components/services/OrthopedicPhysiotherapy';
import ManualTherapy from './components/services/ManualTherapy';
import CardiacRehabilitation from './components/services/CardiacRehabilitation';
import WomensHealth from './components/services/WomensHealth';
import VestibularRehabilitation from './components/services/VestibularRehabilitation';
import HomeCareServices from './components/services/HomeCareServices';
import GeriatricPhysiotherapy from './components/services/GeriatricPhysiotherapy';
import NeurologicalRehabilitation from './components/services/NeurologicalRehabilitation';
import Typed from 'typed.js';
import Blogs from './components/pages/Blogs';

// Import your logo
import logo from './assets/logo.jpg';

const socialButtons = [
  { icon: Facebook, color: '#1877F2', label: 'Facebook', link: 'https://www.facebook.com/people/Careflex-Physiotherapy/pfbid025gnGQC7c9C8DhXJWzrnxkhzCWNCv8qx4uEvXwnYt8qDSTdb178sgwMRgUJqq9qDcl/' },
  { icon: Instagram, color: '#E4405F', label: 'Instagram' },
  { icon: Twitter, color: '#1DA1F2', label: 'Twitter' },
  { icon: LinkedIn, color: '#0A66C2', label: 'LinkedIn' },
  { icon: YouTube, color: '#FF0000', label: 'YouTube' }
];

const StyledAppBar = styled(AppBar)(({ theme, scrolled }) => ({
  background: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  boxShadow: scrolled ? '0 4px 30px rgba(0, 150, 136, 0.15)' : 'none',
  borderBottom: '1px solid rgba(0, 150, 136, 0.1)',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.drawer + 1,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #009688, #4CAF50)',
    opacity: scrolled ? 1 : 0,
    transition: 'opacity 0.4s ease-in-out'
  }
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: '45px',
  width: 'auto',
  border: '2px solid rgba(0, 150, 136, 0.1)',
  borderRadius: '8px',
  padding: '4px',
  [theme.breakpoints.down('sm')]: {
    height: '40px',
  }
}));

const DrawerLogoImage = styled('img')(({ theme }) => ({
  height: '60px',
  width: 'auto',
  borderRadius: '12px',
  margin: '16px auto',
  display: 'block',
  border: '2px solid rgba(0, 150, 136, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05) rotate(-2deg)',
    boxShadow: '0 4px 12px rgba(0, 150, 136, 0.15)'
  }
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: 'linear-gradient(45deg, #009688 30%, #4CAF50 90%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  letterSpacing: '0.5px',
  minHeight: '2.5em',
  '& .typed-cursor': {
    color: '#009688',
    opacity: '1',
    animation: 'typedjsBlink 0.7s infinite'
  },
  '@keyframes typedjsBlink': {
    '50%': {
      opacity: 0.0
    }
  }
}));

const StyledNavButton = styled(Button)(({ theme, active }) => ({
  color: theme.palette.primary.main,
  position: 'relative',
  padding: '8px 16px',
  margin: '0 4px',
  borderRadius: '50px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  overflow: 'hidden',
  background: active ? 'rgba(0, 150, 136, 0.08)' : 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, #009688, #4CAF50)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    zIndex: -1,
    borderRadius: '50px',
  },
  '&:hover': {
    background: 'rgba(0, 150, 136, 0.08)',
    transform: 'translateY(-2px)',
    '&::before': {
      opacity: 0.05,
    }
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
}));

// Add this new styled component for the logo animation
const AnimatedLogoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  opacity: 0,
  transform: 'translateY(20px)',
  animation: 'fadeInUp 0.8s ease forwards',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    }
  }
}));

// Add this new styled component for title animations
const AnimatedTitle = styled(Typography)(({ theme }) => ({
  opacity: 0,
  transform: 'translateX(-20px)',
  animation: 'slideInFade 0.8s ease forwards',
  '@keyframes slideInFade': {
    '0%': {
      opacity: 0,
      transform: 'translateX(-20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    }
  }
}));

// SEO Meta Tags Configuration
const getSEOMetaTags = (pathname) => {
  const baseKeywords = "physiotherapy, physio, exercise, Careflex Physiotherapy, physiotherapy near me, physio near me, physiotherapy clinic near me, physio clinic near by, home physiotherapy, home physiotherapy near me, homecare physiotherapy, home physio, home physio near me, home care physio near by, physiotherapy in Bangalore, physiotherapy in Varthur, physiotherapy in Gunjur, physiotherapist in Whitefield, physiotherapist in Sarjapur, physiotherapist in Koramangala, sports physiotherapy near me Bangalore, back pain physiotherapy, exercise for low back pain, knee pain physiotherapy, knee pain physio near me, shoulder pain physiotherapy, sciatica treatment, cervical spondylosis physiotherapy, lumbar spondylosis physiotherapy, ligament injury physiotherapy, ligament tear physiotherapy, ACL injury physiotherapy, paralysis physiotherapy, facial palsy physiotherapy, stroke physiotherapy, CVA, hemiplegia, arthritis physiotherapy, tennis elbow physiotherapy, slipped disc physiotherapy, frozen shoulder physiotherapy, cupping therapy, cupping therapy at home, dry needling, dry needling at home near me, IFT physiotherapy, TENS physiotherapy, ultrasound therapy physiotherapy, traction physiotherapy, shockwave therapy physiotherapy, laser therapy physiotherapy, manual therapy physiotherapy, myofascial release physiotherapy, ortho physiotherapy, neuro physiotherapy, sports physiotherapy, sports physiotherapy near me, post-surgical physiotherapy, elderly physiotherapy at home, bedridden patient physiotherapy, physiotherapy services Bangalore, best physiotherapist near me, affordable physiotherapy near me, home physiotherapy service Bangalore, mobile physiotherapist near me, physiotherapy clinic in Varthur, physiotherapy clinic in Gunjur, physiotherapist at home Bangalore, private physiotherapy clinic near me, neck pain physiotherapy, lower back pain physiotherapy, herniated disc physiotherapy, post-operative physiotherapy, sports injury physiotherapy, knee replacement physiotherapy, shoulder rehabilitation physiotherapy, frozen shoulder treatment at home, muscle strain physiotherapy, joint pain physiotherapy, plantar fasciitis physiotherapy, whiplash injury physiotherapy, physiotherapy with IFT, TENS therapy at home, ultrasound physiotherapy at home, manual therapy at home, traction therapy at home, cupping therapy Bangalore, dry needling Bangalore, advanced physiotherapy techniques, rehabilitation therapy near me, physiotherapy for ligament tear, elderly physiotherapy near me, bedridden patient physiotherapy, post-stroke physiotherapy, paralysis physiotherapy at home, pediatric physiotherapy at home, women's health physiotherapy, postpartum physiotherapy at home, sports physiotherapy for athletes, best physiotherapy clinic near me in Bangalore, home physiotherapy for knee pain near me, physiotherapist at home in Varthur, shoulder pain treatment at home near me, back pain relief physiotherapy near me, ACL recovery physiotherapy at home, cervical spondylosis exercises at home, home physiotherapy for elderly, postpartum physiotherapy at home, neuro physiotherapy near me, pediatric physiotherapy near me, orthopedic physiotherapy near me";
  
  const seoConfig = {
    '/': {
      title: 'CareFlex Physiotherapy - Best Home Physiotherapy Services in Bangalore | Physio Near Me',
      description: 'Best home physiotherapy services in Bangalore, Varthur, Whitefield, Sarjapur. Expert physiotherapist at home for back pain, knee pain, sports injury, stroke rehabilitation, elderly care. Book appointment for cupping therapy, dry needling, IFT, TENS, manual therapy at home.',
      keywords: baseKeywords + ', best physiotherapy clinic near me in Bangalore, home physiotherapy for knee pain near me, physiotherapist at home in Varthur'
    },
    '/services': {
      title: 'Physiotherapy Services in Bangalore - Orthopedic, Sports, Neuro Rehabilitation | CareFlex',
      description: 'Comprehensive physiotherapy services in Bangalore including orthopedic physiotherapy, sports rehabilitation, neurological rehabilitation, home physiotherapy, cupping therapy, dry needling, IFT, TENS therapy.',
      keywords: baseKeywords + ', orthopedic physiotherapy, sports physiotherapy, neuro physiotherapy, post-surgical physiotherapy, elderly physiotherapy at home, physiotherapy services Bangalore'
    },
    '/contact': {
      title: 'Contact CareFlex Physiotherapy - Best Physiotherapist Near Me in Bangalore | Book Appointment',
      description: 'Contact CareFlex Physiotherapy for home physiotherapy services in Bangalore. Expert physiotherapist at home for back pain, knee pain, sports injury treatment. Call +91 7619678243.',
      keywords: baseKeywords + ', contact physiotherapist, physiotherapy appointment, best physiotherapist near me, physiotherapy clinic contact'
    },
    '/book-appointment': {
      title: 'Book Physiotherapy Appointment Online - Home Physiotherapy Services Bangalore | CareFlex',
      description: 'Book physiotherapy appointment online for home physiotherapy services in Bangalore. Expert physiotherapist at home for back pain, knee pain, sports injury, stroke rehabilitation. Easy online booking.',
      keywords: baseKeywords + ', book physiotherapy appointment, online physiotherapy booking, physiotherapy appointment near me, home physiotherapy booking'
    },
    '/blogs': {
      title: 'Physiotherapy Blog - Exercise Tips, Pain Relief, Rehabilitation Guide | CareFlex',
      description: 'Expert physiotherapy blog with exercise tips, pain relief techniques, rehabilitation guides for back pain, knee pain, sports injury, stroke recovery, elderly care. Latest physiotherapy insights.',
      keywords: baseKeywords + ', physiotherapy blog, exercise tips, pain relief, rehabilitation guide, back pain exercises, knee pain treatment, sports injury recovery'
    }
  };

  return seoConfig[pathname] || seoConfig['/'];
};

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState(localStorage.getItem('whatsappNumber') || '919963595803');
  const [clinicAddress, setClinicAddress] = useState(localStorage.getItem('clinicAddress') || '123 Healing Street\nWellness District\nHealth City, HC 12345');
  const [locations] = useState([
    {
      id: 1,
      name: 'Karnataka - Bengaluru',
      address: 'Bengaluru Branch\nKarnataka, India',
      phone: whatsappNumber
    },
    {
      id: 2,
      name: 'Andhra Pradesh - Giddalur',
      address: 'Giddalur Branch\nAndhra Pradesh, India',
      phone: whatsappNumber
    }
  ]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userAuth, setUserAuth] = useState({
    isAuthenticated: false,
    isAdmin: false,
    user: null
  });
  
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const typedRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const handleStorageChange = () => {
      setWhatsappNumber(localStorage.getItem('whatsappNumber') || '919963595803');
      setClinicAddress(localStorage.getItem('clinicAddress') || '123 Healing Street\nWellness District\nHealth City, HC 12345');
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check for authentication status in localStorage or your auth service
    const checkAuth = () => {
      const authData = localStorage.getItem('authData');
      if (authData) {
        const parsedAuth = JSON.parse(authData);
        setUserAuth(parsedAuth);
        setIsAdmin(parsedAuth.isAdmin);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    // Initialize Typed instance for taglines only
    const typed = new Typed(titleRef.current, {
      strings: [
        'Healing Hands, Caring Hearts',
        'Your Path to Recovery',
        'Expert Care, Better Life',
        'Move Better, Live Better',
        'Excellence in Rehabilitation',
        'Restoring Health & Mobility'
      ],
      typeSpeed: 50,
      backSpeed: 30,
      backDelay: 2000,
      startDelay: 500,
      loop: true,
      showCursor: true,
      cursorChar: '|',
      autoInsertCss: true,
    });

    typedRef.current = typed;

    return () => {
      if (typedRef.current) {
        typedRef.current.destroy();
      }
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleRefresh = () => {
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      window.location.reload();
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      navigate('/');
    }
  };

  const handleLocationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLocationClose = () => {
    setAnchorEl(null);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setAnchorEl(null);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    navigate('/contact-us');
  };

  const navigationItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
    { text: 'Services', icon: <MedicalServices />, path: '/services' },
    { text: 'Blogs', icon: <Article />, path: '/blogs' },
    { text: 'Contact', icon: <ContactPhone />, path: '/contact' },
    { text: 'Book Appointment', icon: <CalendarMonth />, path: '/book-appointment' },
    { text: 'Admin Panel', icon: <AdminPanelSettings />, path: '/admin' }
  ];

  const drawer = (
    <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2,
        borderBottom: '1px solid rgba(0, 150, 136, 0.1)',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)'
      }}>
        <Tooltip title="Home">
          <DrawerLogoImage
            src="new logo.png"
            alt="CAREFLEX PHYSIOTHERAPY"
            onClick={() => {
              handleRefresh();
              handleDrawerToggle();
            }}
            sx={{
              height: '45px',
              margin: 0,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05) rotate(-2deg)'
              }
            }}
          />
        </Tooltip>
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{ 
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(0, 150, 136, 0.08)',
              transform: 'rotate(90deg)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <List sx={{ 
        p: 2,
        flexGrow: 1,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 150, 136, 0.2)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(0, 150, 136, 0.3)'
          }
        }
      }}>
        {navigationItems.map((item, index) => (
          <ListItem 
            key={item.text}
            disablePadding
            sx={{
              mb: 1,
              transform: `translateX(${mobileOpen ? '0' : '-20px'})`,
              opacity: mobileOpen ? 1 : 0,
              transition: `all 0.3s ease ${index * 0.1}s`
            }}
          >
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                handleDrawerToggle();
              }}
              sx={{
                py: 1.5,
                px: 2,
                borderRadius: '12px',
                backgroundColor: location.pathname === item.path ? 'rgba(0, 150, 136, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 150, 136, 0.08)',
                  transform: 'translateX(8px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <ListItemIcon sx={{ 
                color: 'primary.main',
                minWidth: 40,
                transition: 'all 0.3s ease'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{ 
                  '& .MuiListItemText-primary': {
                    color: 'primary.main',
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    transition: 'all 0.3s ease'
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const seoMeta = getSEOMetaTags(location.pathname);

  // Update document head for SEO
  useEffect(() => {
    // Update title
    document.title = seoMeta.title;
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoMeta.description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      metaDescription.setAttribute('content', seoMeta.description);
      document.head.appendChild(metaDescription);
    }
    
    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', seoMeta.keywords);
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      metaKeywords.setAttribute('content', seoMeta.keywords);
      document.head.appendChild(metaKeywords);
    }
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seoMeta.title);
    } else {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      ogTitle.setAttribute('content', seoMeta.title);
      document.head.appendChild(ogTitle);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seoMeta.description);
    } else {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      ogDescription.setAttribute('content', seoMeta.description);
      document.head.appendChild(ogDescription);
    }
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://careflexphysiotherapy.com${location.pathname}`);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `https://careflexphysiotherapy.com${location.pathname}`);
      document.head.appendChild(canonical);
    }
  }, [location.pathname, seoMeta]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ScrollToTop />
      {/* Floating Action Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 20, md: 30 },
          right: { xs: 20, md: 30 },
          zIndex: theme.zIndex.drawer + 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Zoom in={true} style={{ transitionDelay: '500ms' }}>
          <Tooltip title="Call Us" placement="left" arrow>
            <Fab
              href={`tel:+917619678243`}
              sx={{
                width: 55,
                height: 55,
                bgcolor: '#009688',
                color: 'white',
                boxShadow: '0 4px 8px rgba(0, 150, 136, 0.25)',
                '&:hover': {
                  bgcolor: '#00897b',
                  transform: 'translateY(-5px)',
                  boxShadow: '0 6px 12px rgba(0, 150, 136, 0.3)'
                }
              }}
            >
              <Phone sx={{ fontSize: 24 }} />
            </Fab>
          </Tooltip>
        </Zoom>

        <Zoom in={true} style={{ transitionDelay: '700ms' }}>
          <Tooltip title="WhatsApp Us" placement="left" arrow>
            <Fab
              href="https://wa.me/917619678243"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                width: 60,
                height: 60,
                bgcolor: '#25D366',
                color: 'white',
                boxShadow: '0 4px 8px rgba(37, 211, 102, 0.25)',
                '&:hover': {
                  bgcolor: '#22c15e',
                  transform: 'translateY(-5px)',
                  boxShadow: '0 6px 12px rgba(37, 211, 102, 0.3)'
                }
              }}
            >
              <WhatsApp sx={{ fontSize: 28 }} />
            </Fab>
          </Tooltip>
        </Zoom>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: '85%', sm: 320 },
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRight: '1px solid rgba(0, 150, 136, 0.1)',
            boxShadow: '4px 0 15px rgba(0, 0, 0, 0.05)'
          },
        }}
      >
        {drawer}
      </Drawer>

      <StyledAppBar scrolled={scrolled}>
        <Toolbar sx={{ 
          minHeight: { xs: '70px', sm: '80px' },
          px: { xs: 2, sm: 3, md: 4 },
          gap: 2
        }}>
          {/* Menu Icon for Mobile */}
          <IconButton
            color="primary"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              display: { md: 'none' },
              p: 1.5,
              borderRadius: '12px',
              '&:hover': {
                background: 'rgba(0, 150, 136, 0.08)',
                transform: 'rotate(180deg)'
              },
              transition: 'all 0.4s ease'
            }}
          >
            <MenuIcon sx={{ fontSize: '2rem' }} />
          </IconButton>

          {/* Logo and Title */}
          <AnimatedLogoBox
            component={Link} 
            to="/" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: { xs: 1, sm: 2, md: 3 },
              textDecoration: 'none',
              flexGrow: { xs: 1, md: 0 },
              maxWidth: { xs: '100%', md: 'auto' }
            }}
          >
            <Box
              component="img"
              src="/new logo.png"
              alt="CareFlex Physiotherapy"
              sx={{
                height: { xs: '50px', sm: '60px', md: '70px' },
                width: 'auto',
                transition: 'all 0.5s ease',
                filter: scrolled ? 'none' : 'brightness(1.1)',
                animation: 'float 3s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': {
                    transform: 'translateY(0) scale(1)',
                  },
                  '50%': {
                    transform: 'translateY(-10px) scale(1.02)',
                  }
                }
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              visibility: 'visible',
              opacity: 1,
              transform: scrolled ? 'translateY(-5px)' : 'translateY(0)',
              transition: 'all 0.3s ease',
              ml: { xs: 1, sm: 2 }
            }}>
              <AnimatedTitle 
                variant="h4"
                sx={{ 
                  fontWeight: 700,
                  fontSize: { 
                    xs: '1.1rem',
                    sm: '1.3rem',
                    md: '1.8rem',
                    lg: '2rem' 
                  },
                  background: 'linear-gradient(45deg, #009688 30%, #4CAF50 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.5px',
                  fontFamily: "'Playfair Display', serif",
                  lineHeight: 1.2,
                  mb: { xs: 0.2, sm: 0.5 },
                  whiteSpace: 'normal',
                  animationDelay: '0.3s',
                  maxHeight: scrolled ? '40px' : '80px',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                CareFlex Physiotherapy
              </AnimatedTitle>
              <Box 
                sx={{ 
                  minHeight: { xs: '20px', sm: '20px', md: '24px' },
                  display: scrolled ? 'none' : 'block',
                  opacity: scrolled ? 0 : 1,
                  transform: scrolled ? 'translateY(-10px)' : 'translateY(0)',
                  transition: 'all 0.3s ease',
                  animation: 'fadeIn 0.8s ease forwards 0.6s',
                  '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 }
                  }
                }}
              >
                <Typography
                  ref={titleRef}
                  variant="subtitle1"
                  sx={{
                    color: 'primary.main',
                    fontSize: { 
                      xs: '0.75rem',
                      sm: '0.9rem',
                      md: '1rem',
                      lg: '1.1rem' 
                    },
                    fontWeight: 500,
                    fontFamily: "'Poppins', sans-serif",
                    letterSpacing: '1px',
                    lineHeight: 1.2,
                    '& .typed-cursor': {
                      color: '#009688',
                      opacity: '1',
                      animation: 'typedjsBlink 0.7s infinite',
                      fontSize: '1rem'
                    },
                    '& span': {
                      textShadow: '0 2px 4px rgba(0,150,136,0.1)',
                      background: 'linear-gradient(45deg, #009688 30%, #4CAF50 90%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      padding: '0 4px'
                    }
                  }}
                />
              </Box>
            </Box>
          </AnimatedLogoBox>

          {/* Desktop Navigation */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 1,
            ml: 'auto',
            alignItems: 'center'
          }}>
            {navigationItems.map((item) => (
              <StyledNavButton
                key={item.text}
                component={Link}
                to={item.path}
                active={location.pathname === item.path}
                startIcon={item.icon}
              >
                {item.text}
              </StyledNavButton>
            ))}
          </Box>
        </Toolbar>
      </StyledAppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: '56px', sm: '64px' },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/book-appointment" element={<BookAppointment />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/service/:id" element={<ServiceDetail />} />
          <Route path="/sports-physiotherapy" element={<SportsPhysiotherapy />} />
          <Route path="/orthopedic-physiotherapy" element={<OrthopedicPhysiotherapy />} />
          <Route path="/manual-therapy" element={<ManualTherapy />} />
          <Route path="/cardiac-rehabilitation" element={<CardiacRehabilitation />} />
          <Route path="/womens-health" element={<WomensHealth />} />
          <Route path="/vestibular-rehabilitation" element={<VestibularRehabilitation />} />
          <Route path="/home-care-services" element={<HomeCareServices />} />
          <Route path="/geriatric-physiotherapy" element={<GeriatricPhysiotherapy />} />
          <Route path="/neurological-rehabilitation" element={<NeurologicalRehabilitation />} />
        </Routes>
      </Box>

      <Footer />

      {/* Location Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleLocationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            '& .MuiMenuItem-root': {
              py: 1,
              px: 2,
            },
          },
        }}
      >
        {locations.map((location) => (
          <MenuItem
            key={location.id}
            onClick={() => handleLocationSelect(location)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 200,
            }}
          >
            <Place sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="subtitle2">{location.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {location.address.split('\n')[0]}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default App;
